import React, { useEffect, ReactNode, SyntheticEvent } from 'react';
import { FmlxButton, FmlxModal } from 'fmlx-common-ui';

export interface RMModalProps {
  showModal: boolean;
  setShowModal: (flag: boolean) => void;
  size?: FmlxModal.Size;
  onOk?: () => void;
  onCancel?: () => void;
  onSave?: () => void;
  body: any;
  heading?: string;
  saveButtonText?: string;
  okButtonText?: string;
  noButtonText?: string;
  isOkOnly?: boolean;
  isCancelOnly?: boolean;
  isSaveAvailable?: boolean;
  icon?: any;
  titleClr?: string;
  okayDisabled?: boolean;
  saveDisabled?: boolean;
  showButtons?: boolean;
  dialogClassName?: string;
  scrollable?: boolean;
  hideFooter?: boolean;
  showFooterSeparator?: boolean;
  headerClassName?: string;
  headerStyle?: React.CSSProperties;
  onHide?: () => void;
  hideCloseButton?: boolean;
  customHeader?: ReactNode;
  isModalEscapeable?: boolean;
  id?: string;
  inlineMessage?: {
    type: 'error' | 'warning';
    message: string;
  };
  customFooter?: ReactNode;
  okButtonType?: FmlxButton.Type;
  noButtonType?: FmlxButton.Type;
  saveButtonType?: FmlxButton.Type;
  useDefaultButtonLayout?: boolean; // Use the default FmlxModal button positioning
}

type ModalButtonProps = {
  show: boolean;
  disabled: boolean;
  label: string;
  type: 'primary' | 'positive' | 'danger' | 'basic' | FmlxButton.Type;
  variant: 'contain' | 'outline' | 'plain' | FmlxButton.Variant;
  onClick: (event: SyntheticEvent) => void;
};

function RMModal(props: RMModalProps) {
  const {
    showModal,
    setShowModal,
    size = FmlxModal.Size.MEDIUM,
    onOk = () => {},
    onCancel = () => {
      setShowModal(false);
    },
    onSave = () => {},
    body,
    heading,
    saveButtonText = 'Save',
    okButtonText = 'Yes, Proceed',
    noButtonText = 'Cancel',
    isOkOnly = false,
    isCancelOnly = false,
    isSaveAvailable = false,
    okayDisabled = false,
    saveDisabled = false,
    showButtons = true,
    hideCloseButton = false,
    onHide = () => {
      setShowModal(false);
    },
    customHeader,
    isModalEscapeable = true,
    id,
    inlineMessage,
    customFooter,
    okButtonType,
    noButtonType,
    saveButtonType,
    useDefaultButtonLayout,
  } = props;

  const keyDownHandler = (e: KeyboardEvent) => {
    if (e.key === 'Enter' && showModal) {
      onOk();
    }
  };

  useEffect(() => {
    if (onOk && showButtons) {
      document.addEventListener('keydown', keyDownHandler);
      return () => {
        document.removeEventListener('keydown', keyDownHandler);
      };
    }
  }, [showModal, onOk, showButtons]);

  const saveButtonProps: ModalButtonProps = {
    show: showButtons && isSaveAvailable,
    disabled: saveDisabled,
    label: saveButtonText,
    type: saveButtonType ?? FmlxButton.Type.PRIMARY,
    variant: FmlxButton.Variant.CONTAIN,
    onClick: onSave || (() => {}),
  };

  const okayButtonProps: ModalButtonProps = {
    show: showButtons && !isCancelOnly,
    disabled: okayDisabled,
    label: okButtonText,
    type: okButtonType ?? FmlxButton.Type.PRIMARY,
    variant: FmlxButton.Variant.CONTAIN,
    onClick: onOk || (() => {}),
  };

  const noButtonProps: ModalButtonProps = {
    show: showButtons && !isOkOnly,
    disabled: false,
    label: noButtonText,
    type: noButtonType ?? FmlxButton.Type.PRIMARY,
    variant: FmlxButton.Variant.OUTLINE,
    onClick: () => onCancel(),
  };

  return (
    <FmlxModal
      id={id ?? `rmmodal-${heading}-wrapper`}
      open={showModal}
      onCloseClick={onHide}
      title={heading}
      size={size}
      hideCloseButton={hideCloseButton}
      customFooter={props.hideFooter === true && !customFooter ? <area /> : customFooter}
      primaryButton={useDefaultButtonLayout ? okayButtonProps : saveButtonProps}
      secondaryButton={useDefaultButtonLayout ? noButtonProps : okayButtonProps}
      tertiaryButton={useDefaultButtonLayout ? saveButtonProps : noButtonProps}
      customHeader={customHeader}
      disableBlur={!isModalEscapeable}
      inlineMessage={inlineMessage}
    >
      <div id={`rmmodal-body-${id}`} className={`${id}-rmmodal-body`}>
        {body}
      </div>
    </FmlxModal>
  );
}

export default RMModal;
